import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { startCase } from "lodash";
import { MotiView } from "moti";
import { useState } from "react";
import { useFragment } from "react-relay";

import {
  Button,
  ToggleSwitch,
} from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import {
  ArrowLeftIcon,
  CautionMinorOutlineIcon,
  TrashIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  Text,
  VStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { SelectedPaymentMethodFragment$key } from "./__generated__/SelectedPaymentMethodFragment.graphql";
import { useDisableSquareCardOnFile } from "./hooks/useDisableSquareCardOnFile";
import { useSetDefaultCardOnFile } from "./hooks/useSetDefaultCardOnFile";

type PaymentMethodProps = {
  fragmentRef: SelectedPaymentMethodFragment$key | null;
  onClose: () => void;
  refreshQuery: () => void;
};

export const SelectedPaymentMethod = ({
  fragmentRef,
  onClose,
  refreshQuery,
}: PaymentMethodProps) => {
  const card = useFragment(SelectedPaymentMethodFragmentString, fragmentRef);

  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);

  const currentAnimationValue = card
    ? { opacity: 1, right: 0 }
    : { opacity: 0, right: -15 };

  const cardBrand = startCase(card?.cardBrand.toLowerCase());

  const handleDisableSquareCardSuccess = () => {
    refreshQuery();
    onClose();
  };

  const [disableSquareCard, isDisablingSquareCard] = useDisableSquareCardOnFile(
    handleDisableSquareCardSuccess
  );

  const handleDisableSquareCard = () => {
    setIsRemoveCardModalOpen(false);
    disableSquareCard({
      cardId: card?.id ?? "",
    });
  };
  const [setDefaultCardOnFile, isUpdatingDefaultCard] =
    useSetDefaultCardOnFile();

  const { showToast } = useToast();

  const isLoading = isDisablingSquareCard || isUpdatingDefaultCard;

  const isClubCard = card?.user?.id === null || card?.user?.id === undefined;

  const isAttachedToAPackage = (card?.memberPackages?.count || 0) > 0;

  return (
    <>
      <MotiView
        style={{
          position: "absolute",
          top: -80,
          bottom: 0,
          width: "100%",
          pointerEvents: card ? "auto" : "none",
        }}
        transition={{
          opacity: {
            type: "timing",
            duration: 200,
          },
          right: {
            type: "timing",
            duration: 200,
          },
        }}
        from={{ opacity: 0, right: -15 }}
        animate={currentAnimationValue}
        exit={{ opacity: 0, right: -15 }}
      >
        <Box
          flex={1}
          width="100%"
          p={6}
          backgroundColor="surface-primary"
          position="relative"
        >
          {isLoading ? <Loader /> : null}

          <Pressable
            backgroundColor="grayscale-100"
            width={56}
            height={56}
            p={5}
            style={{ borderRadius: 8 }}
            justifyContent="center"
            alignItems="center"
            onPress={onClose}
          >
            <ArrowLeftIcon color="grayscale-1000" />
          </Pressable>

          <Text pt={8} pb={2} variant="mainTitle2" color="text-primary">
            {cardBrand} {card?.lastFour}
          </Text>
          <VStack gap={6} mt={6}>
            <HStack alignItems="center" justifyContent="space-between" py={2}>
              <Text variant="mainBaseSemiBold">{t`Expires`}</Text>
              <Text variant="mainBaseSemiBold">
                {card?.expMonth}/{card?.expYear}
              </Text>
            </HStack>
            <HStack alignItems="center" justifyContent="space-between" py={2}>
              <Text variant="mainBaseSemiBold">{t`Default Card`}</Text>
              <ToggleSwitch
                isControlled
                isDisabled={isClubCard}
                isChecked={!!card?.default}
                onToggle={(isChecked) => {
                  if (isChecked) {
                    setDefaultCardOnFile({ squareCardId: card?.id ?? "" });
                  } else {
                    showToast({
                      description: t`You must have one default payment method.`,
                      semanticStyle: "info",
                      toastId: `card-already-default`,
                      testID: `card-already-default`,
                    });
                  }
                }}
              />
            </HStack>
          </VStack>
          <VStack
            flex={1}
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            {isAttachedToAPackage ? (
              <InfoBanner
                leadingIcon={CautionMinorOutlineIcon}
                message={t`This card is linked to a subscription and cannot be removed while it remains connected to it.`}
                colorTheme="WARNING"
              />
            ) : null}
            <Button
              variant="danger"
              width="100%"
              leftIcon={<TrashIcon />}
              onPress={() => setIsRemoveCardModalOpen(true)}
              isDisabled={isAttachedToAPackage}
              accessibilityLabel={t`Remove this Card button`}
            >
              {t`Remove this Card`}
            </Button>
          </VStack>
        </Box>
      </MotiView>
      <PopUpModal
        theme="danger"
        upperTitle={t`Remove this Card`}
        isOpen={isRemoveCardModalOpen}
        cancelText={t`Dismiss`}
        confirmText={t`Remove`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleDisableSquareCard();
          } else {
            setIsRemoveCardModalOpen(false);
          }
        }}
        content={
          <VStack>
            <Text
              variant="mainBaseMedium"
              color="text-secondary"
            >{t`Are you sure you want to permanently remove this payment method?`}</Text>
          </VStack>
        }
      />
    </>
  );
};

const SelectedPaymentMethodFragmentString = graphql`
  fragment SelectedPaymentMethodFragment on SquareCardOnFile {
    id
    cardBrand
    enabled
    expMonth
    expYear
    lastFour
    default
    user {
      id
    }
    memberPackages {
      count
    }
  }
`;
